import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'services/store';
import { apiLogin } from './api';
import type { ConnectionInfos, User } from './types';

type AuthState = {
  isAuthReady: boolean;
  user: ConnectionInfos | null;
  token: string | null;
}

const initialState: AuthState = { isAuthReady: false, user: null, token: null };

const mergeUser = (state: AuthState, user: User) => {
  if (state.user) {
    state.user = { ...state.user, ...user };
  } else {
    state.user = user as ConnectionInfos;
  }
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      {
        payload: { user },
      }: PayloadAction<{ user: ConnectionInfos | null; }>,
    ) => {
      state.user = user;
      state.isAuthReady = true;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      apiLogin.endpoints.getSession.matchFulfilled,
      (state, { payload }) => {
        state.user = payload;
        state.isAuthReady = true;
      },
    );
    builder.addMatcher(
      apiLogin.endpoints.getSession.matchRejected,
      state => {
        state.isAuthReady = true;
      },
    );
    builder.addMatcher(
      apiLogin.endpoints.patchUserMe.matchFulfilled,
      (state, { payload }) => mergeUser(state, payload),
    );
    builder.addMatcher(
      apiLogin.endpoints.putUserMePicture.matchFulfilled,
      (state, { payload }) => mergeUser(state, payload),
    );
    builder.addMatcher(
      apiLogin.endpoints.deleteUserMePicture.matchFulfilled,
      (state, { payload }) => mergeUser(state, payload),
    );
  },
});

export const { setCredentials } = slice.actions;

export default slice.reducer;

export const selectCurrentUser = (state: RootState) => {
  return state.auth.user;
}

export const selectIsAuthReady = (state: RootState) => {
  return state.auth.isAuthReady;
}
